import { motion } from "framer-motion";
import { Stethoscope, Smartphone, Truck, Check } from "lucide-react";

const PILLARS = [
  {
    icon: Stethoscope,
    eyebrow: "HCP Connect",
    title: "Healthcare professional engagement, on the ground.",
    body: "Young pharmacy graduates from Tier 2–3 cities — comprehensively trained, highly motivated, locally fluent.",
    metric: { label: "HCP Touchpoints", value: "55,000+" },
    bullets: [
      "Direct doctor & specialist engagement",
      "Brand consolidation relationships",
      "Medical training programs",
      "Clear career progression",
    ],
  },
  {
    icon: Smartphone,
    eyebrow: "Digital Connect",
    title: "Remote engagement & scalability — without proportional cost.",
    body: "An in-house digital team covering pan-India, complementing field reach with always-on touch.",
    metric: { label: "Digital Calls / Day", value: "40–50" },
    bullets: [
      "40–50 digital calls per person daily",
      "Virtual CME programs & medical updates",
      "WhatsApp-based engagement",
      "Practice management & certification",
    ],
  },
  {
    icon: Truck,
    eyebrow: "Channel Connect",
    title: "Distribution optimized — availability, compliance, zero stockouts.",
    body: "Strategic hub-chemist partnerships ensuring product is on the shelf when prescriptions hit.",
    metric: { label: "Chemist Network", value: "60,000+" },
    bullets: [
      "End-to-end logistics management",
      "Inventory optimization & monitoring",
      "GST & compliance assistance",
      "Prevention of expiries & stockouts",
    ],
  },
];

export default function Phygital() {
  return (
    <section id="solutions" className="relative py-20 md:py-32 bg-[#0B0F19] text-white overflow-hidden" data-testid="phygital-section">
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <div className="max-w-3xl mb-14 md:mb-20">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-teal-300 mb-3">3D Phygital Engine</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.02]">
            HCP + Digital + Channel <br />
            <span className="font-editorial italic font-medium text-slate-300">= complete revitalization.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.eyebrow}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`phygital-pillar-${i}`}
                className="bg-white/[0.04] border border-white/10 p-7 md:p-8 backdrop-blur-sm hover:bg-white/[0.07] transition-colors"
              >
                <div className="flex items-center justify-between mb-8">
                  <Icon className="h-7 w-7 text-teal-300" strokeWidth={1.5} />
                  <div className="text-right">
                    <div className="font-display font-extrabold text-2xl tnum">{p.metric.value}</div>
                    <div className="text-[11px] tracking-[0.14em] uppercase text-slate-400 font-semibold">
                      {p.metric.label}
                    </div>
                  </div>
                </div>
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal-300 mb-2">
                  {p.eyebrow}
                </div>
                <h3 className="font-display text-xl md:text-2xl font-bold mb-4 leading-[1.2]">{p.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-6">{p.body}</p>
                <ul className="space-y-2.5 border-t border-white/10 pt-5">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <Check className="h-4 w-4 text-teal-300 mt-0.5 shrink-0" strokeWidth={2} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
